import { useEffect, useMemo, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useMemoizedFn, useRequest, useSafeState, useUnmount } from 'ahooks';
import svgPanZoom from 'svg-pan-zoom';
import SVGTool from './svgTool';
import { queryFileInfoEpc } from '@/components/FileUpload/service/epc/utils';
import { message, Spin, Space, Empty } from 'antd';
import { EVENTTYPES } from './types';
import { CLICKFROM, STATUSTYPE } from '../utils';
import type { SelectValue } from '../utils';
import { useReactToPrint } from 'react-to-print';
import PrintA4 from '@/components/PrintA4';
import type { Part } from '@/api/epc/catalog/usageUsingPOST';
import { type VehicleInfo as VehicleInfoType } from '@/api/queryCatalog';

import styles from './index.module.less';

type LegendProps = {
  fileId: string;
  selectValue: SelectValue;
  validTexts: number[];
  setSelectValue: (values: SelectValue) => void;
  defaultCallout: number;
  legendName: string;
  fileIdLoading: boolean;
  partList: Part[];
  basicInfo: VehicleInfoType & {
    legendName: string;
  };
};

const SVGEmpty = () => {
  return (
    <Empty
      style={{ minWidth: '100%', paddingTop: '30vh' }}
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description='暂无图片'
    />
  );
};

const LegendSVG = (props: LegendProps) => {
  const { fileId, selectValue, setSelectValue, validTexts, defaultCallout, partList, basicInfo } =
    props;
  const svgWrapRef = useRef<HTMLDivElement | null>(null);
  const panZoomInstance = useRef<SvgPanZoom.Instance | null>(null);
  const svgToolInstance = useRef<SVGTool | null>(null);
  const [svgContent, setSvgContent] = useSafeState<string | null>(null);
  const [loading, setLoading] = useSafeState<boolean>(false);
  const svgUrl = useRef<string | null>(null);

  // 动态加载 SVG 文件
  useRequest(
    async () => {
      if (!fileId) return setSvgContent(null);
      setLoading(true);
      try {
        const res = await queryFileInfoEpc([fileId]);
        if (res && res.length) {
          svgUrl.current = res[0].url;
          const response = await fetch(res[0].url);
          const svgText = await response.text();
          const svgTagMatch = svgText.match(/<svg[^>]*>[\s\S]*<\/svg>/i);
          if (response.status === 200 && svgTagMatch) {
            setSvgContent(svgTagMatch[0]);
          } else setSvgContent(null);
        } else {
          message.error('尚未维护图片，请联系备件中台维护！');
          setSvgContent(null);
        }
      } catch (error) {
        setSvgContent(null);
      } finally {
        setLoading(false);
      }
    },
    { manual: false, refreshDeps: [fileId] }
  );

  const onLegendTextClick = useMemoizedFn((text: string) => {
    const value = Number(text);
    setSelectValue({
      callout: value,
      partNo: '',
      source: CLICKFROM.LEGEND,
      type: STATUSTYPE.HIGHLIGHT
    });
  });

  useEffect(() => {
    if (selectValue.callout && selectValue.source === CLICKFROM.PART) {
      svgToolInstance.current?.outsideHighlight(String(selectValue.callout));
    }
  }, [selectValue]);

  const destroyToolAndInstance = useMemoizedFn(() => {
    try {
      panZoomInstance.current?.destroy();
      panZoomInstance.current = null;
      if (svgWrapRef.current) {
        svgWrapRef.current.innerHTML = '';
      }
      svgToolInstance.current?.destroy();
    } catch (e) {
      console.log('destroyToolAndInstance error:', e);
    }
  });

  useEffect(() => {
    destroyToolAndInstance();
    if (svgWrapRef.current) {
      if (!svgContent) {
        svgWrapRef.current.innerHTML = ReactDOMServer.renderToString(<SVGEmpty />);
      } else {
        svgWrapRef.current.innerHTML = svgContent;
        const svgElement = svgWrapRef.current.querySelector('svg');
        if (svgElement) {
          panZoomInstance.current = svgPanZoom(svgElement, {
            zoomEnabled: true,
            controlIconsEnabled: false,
            center: false,
            fit: false
          });
          svgToolInstance.current = new SVGTool({
            svgElement,
            validTexts: validTexts,
            defaultCallout: defaultCallout
          });
          svgToolInstance.current.subscribe(EVENTTYPES.TEXTCLICK, onLegendTextClick);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svgContent]);

  useUnmount(() => {
    destroyToolAndInstance();
  });

  const handleZoomIn = useMemoizedFn(() => {
    if (panZoomInstance.current) {
      panZoomInstance.current.zoomIn();
    }
  });

  const handleZoomOut = useMemoizedFn(() => {
    if (panZoomInstance.current) {
      panZoomInstance.current.zoomOut();
    }
  });

  const handleReset = useMemoizedFn(() => {
    if (panZoomInstance.current) {
      panZoomInstance.current.resetZoom();
      panZoomInstance.current.resetPan();
    }
  });

  const printRef = useRef(null);
  const printInfos = useMemo(() => {
    const { brandName, modelName, seriesName, legendName } = basicInfo;
    return [brandName, modelName, seriesName, legendName];
  }, [basicInfo]);
  const print = useReactToPrint({
    content: () => printRef.current,
    documentTitle: printInfos.join('_'),
    copyStyles: true,
    onBeforeGetContent: () => {},
    onAfterPrint: () => {},
    removeAfterPrint: true,
    pageStyle: PrintA4.pageStyle
  });

  const handlePrint = useMemoizedFn(async () => {
    Promise.resolve().then(() => {
      print();
    });
  });

  return (
    <div className={styles.legendContainer}>
      <Spin spinning={loading || props.fileIdLoading}>
        <div className={styles.title}>{basicInfo.legendName}</div>
        <Space className={styles.controls} size={16}>
          <img src={require('../assets/zoomin.png')} alt='' onClick={handleZoomIn} />
          <img src={require('../assets/zoomout.png')} alt='' onClick={handleZoomOut} />
          <img src={require('../assets/reset.png')} alt='' onClick={handleReset} />
          <img src={require('../assets/print.png')} alt='' onClick={handlePrint} />
        </Space>
        <div className={styles.svgWrap} ref={svgWrapRef}>
          <SVGEmpty />
        </div>
      </Spin>
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          <PrintA4 title={basicInfo?.legendName ?? ''}>
            {
              <>
                <div className='info' style={{ justifyContent: 'center' }}>
                  {printInfos.join('/')}
                </div>
                {svgContent && svgUrl.current && (
                  <div className='info'>
                    <img style={{ width: '100%' }} src={svgUrl.current} alt='' />
                  </div>
                )}
                <div className='info' style={{ justifyContent: 'center' }}>
                  详细 (共 {partList?.length ?? 0} 行数据)
                </div>
                <table className='table'>
                  <thead>
                    <tr style={{ textAlign: 'left' }}>
                      <th>序号</th>
                      <th>配件编码</th>
                      <th>配件名称</th>
                      <th>数量</th>
                      <th>用法备注</th>
                    </tr>
                  </thead>
                  {partList?.map(item => {
                    return (
                      <tbody key={item.partNo}>
                        <tr>
                          <td>{item.callout}</td>
                          <td>{item.partNo}</td>
                          <td>{item.partName}</td>
                          <td>{item.qty}</td>
                          <td>{item.note}</td>
                        </tr>
                      </tbody>
                    );
                  })}
                </table>
              </>
            }
          </PrintA4>
        </div>
      </div>
    </div>
  );
};

export default LegendSVG;
